import React, { Dispatch, SetStateAction } from 'react';
import { useDispatch } from 'react-redux';
import { IParams } from 'src/models/card';
import { itemBuckets } from 'src/priceCalculation/buckets';
import { ADDITIONAL_ADDED } from 'src/priceCalculation/constants';
import { modifiersAgainstAnItem } from 'src/priceCalculation/helper';

import * as Constants from '../../constants';
import {
  ICoreModifiers,
  IModifierGroups,
  IModifiersObjects,
  IRequiredModifier,
  ItemDetails,
} from '../../models/item.model';
import {
  removeTemporarilyAddedPriceForItem,
  updateSpecificItemSelectedModifiers,
} from '../../redux/slices/itemCustomizationSlice';

import CoreModifiers from './ingredients/CoreModifiers';
import ModifierGroups from './ingredients/ModifierGroups';
import RequiredModifier from './ingredients/RequiredModifier';
import ItemsAsModifiers from './ItemsAsModifiers';
import { IItemAsModifiers } from 'src/models/order';
import { filterVisibleSubItemModifierGroups } from 'src/helper/sandwichTrayChips.helper';

type propsType = {
  coreModifiers: ICoreModifiers[];
  handleIngredientSelection: (data: any) => void;
  modifierGroups: IModifierGroups;
  requiredModifiers: IRequiredModifier;
  itemName: string;
  showCustomization: boolean;
  itemModifierItemModalWidth?: string;
  cardColSize?: string;
  itemsAsModifiers?: IItemAsModifiers[];
  isPackege?: boolean;
  itemAsModifierState?: {
    itemsAsModifier: IItemAsModifiers[];
    setItemsAsModifier: Dispatch<SetStateAction<any[]>>;
  };
  isCreateYourOwnItem?: boolean;
  CYOIScrollToMissingRequiredArea?: boolean;
  itemsAsModifiersToHandleScroll?: {
    refToScrollToMissingRequiredItemAsModifiers: any[];
    isItemAsModifierSelected: {
      status: boolean;
      highLightArea: number;
    };
    setIsItemAsModifierSelected: React.Dispatch<
      React.SetStateAction<{ status: boolean; highLightArea: any }>
    >;
  };
  isCYOIColSize?: string;
  selectedItem: 1 | 2 | 3 | 4;
  openItemInfoModal?: (item: ItemDetails) => void;
  /** Tray line item whose sub_items_required_modifier_groups are shown (sandwich tray, package tray row, etc.) */
  parentTrayItem?: ItemDetails;
};

const AddIngredients: React.FC<propsType> = (props) => {
  const {
    coreModifiers,
    handleIngredientSelection,
    modifierGroups,
    requiredModifiers,
    itemName,
    showCustomization,
    itemModifierItemModalWidth,
    cardColSize,
    itemsAsModifiers,
    isPackege,
    itemAsModifierState,
    isCreateYourOwnItem,
    CYOIScrollToMissingRequiredArea,
    itemsAsModifiersToHandleScroll,
    isCYOIColSize,
    selectedItem,
    openItemInfoModal,
    parentTrayItem,
  } = props;

  const dispatchItemCustomization = useDispatch();

  const coreModifierBucket = (function () {
    return itemBuckets.getSingleBucket({
      name: Constants.CORE_MODIFIERS,
      fromItem: selectedItem,
    });
  })();
  const isItCoreRelated = (groupId: number) => {
    if (coreModifierBucket && coreModifierBucket['memoryChip']) {
      const isExist = coreModifierBucket['memoryChip'][groupId];
      if (isExist === undefined) {
        return false;
      } else {
        return true;
      }
    }
  };

  const selectedAddedAddOns: any[] = (function () {
    const fromItem: any = selectedItem - 1;
    return itemBuckets.getSingleBucketKeyValue({
      name: Constants.ADD_ONS,
      fromItem,
      modifierType: ADDITIONAL_ADDED,
      key: 'modifiers',
    });
  })();

  const isLimitExceed = (
    groupId: number,
    extendable_limit: number,
    groupMin,
  ) => {
    if (extendable_limit === 1 && groupMin === 1) {
      return false;
    } else {
      if (!isItCoreRelated(groupId)) {
        return selectedAddedAddOns?.length >= extendable_limit ? true : false;
      } else {
        if (coreModifierBucket['memoryChip'][groupId]?.changes) {
          return coreModifierBucket['memoryChip'][groupId]?.changes?.quantity >=
            extendable_limit
            ? true
            : false;
        } else {
          return coreModifierBucket['memoryChip'][groupId]?.quantity >=
            extendable_limit
            ? true
            : false;
        }
      }
    }
  };

  const selectedCore: any[] = (function () {
    return itemBuckets.specificItemBucketSelectedModifiers(
      Constants.CORE_MODIFIERS,
      selectedItem,
    );
  })();

  const handleCoreModifierClick = (
    data: IParams,
    modifier: ICoreModifiers,
    type: string,
  ) => {
    handleIngredientSelection({
      ...data,
      ...{
        modifier_group_id: modifier?.modifier_group_id,
        modifier_type: type,
      },
    });
  };

  const selectedAddOns = (function () {
    return itemBuckets.specificItemBucketSelectedModifiers(
      Constants.ADD_ONS,
      selectedItem,
    );
  })();

  const handleClick = (
    data: IParams,
    modifierGroup: IModifierGroups,
    type: string,
  ) => {
    handleIngredientSelection({
      ...data,
      ...{
        modifier_group_id: modifierGroup?.id,
        modifier_group_max: modifierGroup?.max,
        modifier_group_base: modifierGroup?.base,
        modifier_type: type,
      },
    });
  };

  const noDressingHandler = (modifier: any, itemNo) => {
    dispatchItemCustomization(
      removeTemporarilyAddedPriceForItem({
        price: itemBuckets.resetChangesOnNoDreesing(itemNo, modifier),
        item: itemNo,
      }),
    );
    dispatchItemCustomization(
      updateSpecificItemSelectedModifiers({
        itemModifiers: modifiersAgainstAnItem({
          itemId: selectedItem,
        }),
        itemId: selectedItem.toString(),
      }),
    );
  };

  const subItemGroupsList = itemsAsModifiers ?? [];
  const visibleItemsAsModifiers =
    parentTrayItem && subItemGroupsList.length > 0
      ? filterVisibleSubItemModifierGroups(
          subItemGroupsList,
          parentTrayItem,
          selectedItem,
        )
      : subItemGroupsList;

  const showItemsAsModifiers = () => {
    if (!itemAsModifierState || visibleItemsAsModifiers.length === 0) {
      return null;
    }
    return (
      <ItemsAsModifiers
        itemAsModifiers={visibleItemsAsModifiers}
        cardColSize={cardColSize}
        isPackege={isPackege}
        itemAsModifierState={itemAsModifierState}
        itemsAsModifiersToHandleScroll={itemsAsModifiersToHandleScroll}
        openItemInfoModal={openItemInfoModal}
        parentTrayItem={parentTrayItem}
        subItemBucketSlot={selectedItem}
      />
    );
  };

  return (
    <>
      {isCreateYourOwnItem ? (
        <div className={`${isPackege && 'ps-md-2 packagesModifiers'}`}>
          <RequiredModifier
            sectionSelected={selectedItem.toString()}
            itemIngredients={requiredModifiers}
            handleIngredientSelection={handleIngredientSelection}
            noDressingHandler={noDressingHandler}
            showMaxSelection={isCreateYourOwnItem ? false : true}
            customizationModule={false}
            showAdditionalPrice={true}
            isCYOIColSize={isCYOIColSize}
            itemModifierItemModalWidth={itemModifierItemModalWidth}
            isCreateYourOwnItem={isCreateYourOwnItem}
            CYOIScrollToMissingRequiredArea={CYOIScrollToMissingRequiredArea}
          />
          <ModifierGroups
            modifierGroups={modifierGroups}
            selectedAddOns={selectedAddOns}
            selectedCore={selectedCore}
            handleClick={handleClick}
            isLimitExceed={isLimitExceed}
            showCustomization={showCustomization}
            itemModifierItemModalWidth={itemModifierItemModalWidth}
            cardColSize={cardColSize}
            isPackege={isPackege}
          />
        </div>
      ) : (
        <div className={`${isPackege && 'ps-md-2 packagesModifiers'}`}>
          {showItemsAsModifiers()}
          <CoreModifiers
            coreModifiers={coreModifiers}
            selectedCore={selectedCore}
            handleCoreModifierClick={handleCoreModifierClick}
            isLimitExceed={isLimitExceed}
            cardColSize={cardColSize}
            isPackege={isPackege}
          />
          <ModifierGroups
            modifierGroups={modifierGroups}
            selectedAddOns={selectedAddOns}
            selectedCore={selectedCore}
            handleClick={handleClick}
            isLimitExceed={isLimitExceed}
            showCustomization={showCustomization}
            itemModifierItemModalWidth={itemModifierItemModalWidth}
            cardColSize={cardColSize}
            isPackege={isPackege}
          />
        </div>
      )}
    </>
  );
};

export default AddIngredients;
